import axios from 'axios';
import apiRoutes from './apiRoutes';
import { getActorType, getGlobalAccessToken, globalLogout, setGlobalAccessToken } from './tokenService';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// V2 lives under /api/v2. The env var keeps the .../api root; we append the version here
// so route paths mirror the V2 doc (and bumping the version is a one-line change).
const api = axios.create({
  baseURL: `${API_BASE_URL}/v2`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // required: the refresh token is an HttpOnly cookie
});

// Single-flight refresh: when several requests 401 at once (access token just
// expired), they all queue behind ONE /auth/refresh call instead of each firing
// its own. Prevents the refresh-token burst that could log the user out.
let refreshPromise = null;

api.interceptors.request.use(
  (config) => {
    const token = getGlobalAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// --------------------
// Response Interceptor
// --------------------
// V2 returns the DTO directly (no envelope). We re-synthesize the legacy
// `{ isSuccess, result }` shape so the existing services/components keep working
// unchanged (they read `response.result`).
api.interceptors.response.use(
  // Successful response handler (axios resolves all 2xx):
  (response) => {
    return { isSuccess: true, result: response.data };
  },
  // Error response handler:
  async (error) => {
    if (error.response) {
      const originalRequest = error.config;
      const status = error.response.status;
      const data = error.response.data;

      // V2 error body is `{ "error": "message" }`. Expose a legacy `errorMessages[]`
      // alongside it so older consumers keep working. English text is kept as-is.
      if (data && typeof data === 'object' && !data.errorMessages) {
        data.errorMessages = data.error ? [data.error] : ['Unknown error'];
      }

      // Maintenance mode
      if (status === 503) {
        window.dispatchEvent(new CustomEvent('maintenance-mode'));
        return Promise.reject(new Error('النظام في وضع الصيانة، يرجى المحاولة لاحقاً'));
      }

      const excludedEndpoints = [
        apiRoutes.employeeLogin,
        apiRoutes.refreshToken,
        apiRoutes.employeeLogout,
      ];

      if (excludedEndpoints.some((endpoint) => originalRequest.url.includes(endpoint))) {
        return Promise.reject(error);
      }

      if (status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        try {
          // All concurrent 401s share ONE refresh call. Capture the promise
          // locally so the `.finally` reset below can't null it out from under us.
          let pending = refreshPromise;
          if (!pending) {
            // Refresh token rides the HttpOnly cookie; body only carries the actor type.
            pending = api
              .post(apiRoutes.refreshToken, { actorType: getActorType() })
              .then((res) => (res && res.result ? res.result.accessToken : null))
              .finally(() => {
                refreshPromise = null;
              });
            refreshPromise = pending;
          }

          const newAccessToken = await pending;
          if (newAccessToken) {
            setGlobalAccessToken(newAccessToken);
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return api(originalRequest);
          }
          globalLogout();
          return Promise.reject(new Error('انتهت صلاحية هذه الجلسة'));
        } catch (refreshError) {
          globalLogout();
          return Promise.reject(refreshError);
        }
      }

      // Normalize into an Error that still carries the response + a readable message.
      const message =
        (data && (data.error || (data.errorMessages && data.errorMessages[0]))) ||
        error.message;
      const normalized = new Error(message);
      normalized.response = error.response;
      normalized.status = status;
      return Promise.reject(normalized);
    }

    return Promise.reject(error);
  }
);

export default api;
